// Browser-compatible Yggdrasil server shim
// Handles both proxy-based auth (Microsoft) and direct Yggdrasil auth (Blessing Skin)

function mcHexDigest (hashBuf: Buffer): string {
  const negative = hashBuf[0]! > 127
  if (negative) {
    let carry = true
    for (let i = hashBuf.length - 1; i >= 0; --i) {
      const newByte = ~hashBuf[i]! & 0xff
      if (carry) {
        carry = newByte === 0xff
        hashBuf[i] = carry ? 0 : newByte + 1
      } else {
        hashBuf[i] = newByte
      }
    }
  }
  let hex = hashBuf.toString('hex').replace(/^0+/, '')
  if (negative) hex = '-' + hex
  return hex
}

async function computeServerHash (serverId: string, sharedSecret: any, publicKey: any): Promise<string> {
  const encoder = new TextEncoder()
  const serverIdBytes = encoder.encode(serverId)
  const secretBytes = sharedSecret instanceof Uint8Array ? sharedSecret : new Uint8Array(Buffer.from(sharedSecret))
  const keyBytes = publicKey instanceof Uint8Array ? publicKey : new Uint8Array(Buffer.from(publicKey))

  const combined = new Uint8Array(serverIdBytes.length + secretBytes.length + keyBytes.length)
  combined.set(serverIdBytes, 0)
  combined.set(secretBytes, serverIdBytes.length)
  combined.set(keyBytes, serverIdBytes.length + secretBytes.length)

  const hashBuffer = await crypto.subtle.digest('SHA-1', combined)
  return mcHexDigest(Buffer.from(hashBuffer))
}

export const server = ({ host: sessionServer }) => {
  // Check if this is a direct Yggdrasil server (not a proxy)
  const isDirectYggdrasil = sessionServer && (
    sessionServer.includes('/api/yggdrasil') ||
    sessionServer.includes('/sessionserver')
  )

  return {
    async join (accessToken, sessionSelectedProfileId, serverId, sharedSecret, publicKey, cb) {
      try {
        if (isDirectYggdrasil) {
          // Direct Yggdrasil auth - compute server hash client-side
          const serverHash = await computeServerHash(serverId, sharedSecret, publicKey)
          const joinUrl = sessionServer.endsWith('/') ? `${sessionServer}session/minecraft/join` : `${sessionServer}/session/minecraft/join`
          const result = await fetch(joinUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken,
              selectedProfile: sessionSelectedProfileId,
              serverId: serverHash,
            }),
          })
          if (!result.ok) {
            const text = await result.text()
            throw new Error(`Yggdrasil session join failed (${result.status}): ${text}`)
          }
        } else {
          // Proxy-based auth (Microsoft) - send raw data to proxy
          const result = await fetch(`${sessionServer}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken,
              selectedProfile: sessionSelectedProfileId,
              serverId,
              sharedSecret,
              publicKey,
            }),
          })
          if (!result.ok) {
            throw new Error(`Request failed ${await result.text()}`)
          }
        }
        cb(null)
      } catch (err) {
        cb(err)
      }
    }
  }
}
